import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/hooks/useAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.76, 0, 0.24, 1];

const errorText = (d) =>
    !d
        ? "Something went wrong."
        : typeof d === "string"
            ? d
            : Array.isArray(d)
                ? d.map((e) => e?.msg || "").filter(Boolean).join(" ")
                : d?.msg || "Something went wrong.";

export default function AuthModal({ open, mode: initialMode = "login", onClose }) {
    const { setSession } = useAuth();
    const [mode, setMode] = useState(initialMode);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [nickname, setNickname] = useState("");
    const [stayInformed, setStayInformed] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setMode(initialMode);
            setError(null);
        }
    }, [open, initialMode]);

    useEffect(() => {
        if (!open) return;
        const k = (e) => e.key === "Escape" && onClose?.();
        window.addEventListener("keydown", k);
        return () => window.removeEventListener("keydown", k);
    }, [open, onClose]);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
            const url = mode === "login" ? "/auth/login" : "/auth/register";
            const payload =
                mode === "login"
                    ? { email, password }
                    : { email, name, nickname, password, stay_informed: stayInformed };
            const res = await axios.post(`${API}${url}`, payload);
            setSession(res.data.token, res.data.user);
            onClose?.();
        } catch (err) {
            setError(errorText(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="auth-modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[110] flex items-center justify-center px-4"
                >
                    <div
                        className="absolute inset-0 backdrop-blur-md"
                        style={{ background: "rgba(13,19,23,0.85)" }}
                        onClick={onClose}
                    />
                    <motion.form
                        onSubmit={submit}
                        initial={{ y: 24, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 24, opacity: 0 }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="relative z-10 w-full max-w-md bg-[color:var(--cloud)] border border-[color:var(--charcoal)] p-8 md:p-10"
                    >
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="auth-close"
                            className="absolute top-3 right-3 h-8 w-8 flex items-center justify-center rounded-full border border-[color:var(--charcoal)]/30 hover:bg-[color:var(--charcoal)] hover:text-white transition-colors"
                            aria-label="Close"
                        >
                            <X size={13} strokeWidth={1.6} />
                        </button>

                        <div className="font-mono text-[12px] uppercase tracking-[0.26em] text-[color:var(--slate)] mb-3">
                            {mode === "login" ? "Sign in" : "Create an account"}
                        </div>
                        <h2 className="font-display text-[1.8rem] md:text-[2.4rem] leading-[0.95] tracking-[-0.02em] mb-6">
                            {mode === "login" ? "Welcome back." : "Join the discussion."}
                        </h2>

                        {mode === "register" && (
                            <>
                                <label className="block mb-4">
                                    <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                                        Full name
                                    </span>
                                    <input
                                        type="text"
                                        required
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        data-testid="auth-name"
                                        className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                                    />
                                </label>
                                <label className="block mb-4">
                                    <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                                        Nickname (shown publicly)
                                    </span>
                                    <input
                                        type="text"
                                        required
                                        value={nickname}
                                        onChange={(e) => setNickname(e.target.value)}
                                        data-testid="auth-nickname"
                                        className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                                    />
                                </label>
                            </>
                        )}
                        <label className="block mb-4">
                            <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                                Email
                            </span>
                            <input
                                type="email"
                                required
                                value={email}
                                autoComplete={mode === "login" ? "username" : "email"}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="auth-email"
                                className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                            />
                        </label>
                        <label className="block mb-6">
                            <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                                Password {mode === "register" && "(8+ chars)"}
                            </span>
                            <input
                                type="password"
                                required
                                minLength={mode === "register" ? 8 : undefined}
                                value={password}
                                autoComplete={mode === "login" ? "current-password" : "new-password"}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="auth-password"
                                className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                            />
                        </label>

                        {mode === "register" && (
                            <label className="mb-6 flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={stayInformed}
                                    onChange={(e) => setStayInformed(e.target.checked)}
                                    data-testid="auth-stay-informed"
                                    className="mt-1 h-4 w-4 accent-[color:var(--charcoal)]"
                                />
                                <span>
                                    <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-[color:var(--charcoal)]">
                                        Stay Informed
                                    </span>
                                    <span className="block font-body text-sm text-[color:var(--charcoal)]/85 mt-1">
                                        Send me a brief note when a new chapter,
                                        article, or podcast episode drops.
                                    </span>
                                </span>
                            </label>
                        )}

                        {error && (
                            <div data-testid="auth-error" className="mb-4 p-3 border border-[color:var(--charcoal)] font-body text-sm">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="auth-submit"
                            className="pill-btn w-full justify-center disabled:opacity-60"
                        >
                            {busy ? (
                                <>
                                    <Loader2 size={14} strokeWidth={1.7} className="animate-spin" />
                                    {mode === "login" ? "Signing in" : "Creating"}
                                </>
                            ) : mode === "login" ? (
                                "Sign in"
                            ) : (
                                "Create account"
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={() => setMode(mode === "login" ? "register" : "login")}
                            data-testid="auth-toggle-mode"
                            className="w-full mt-4 font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] hover:text-[color:var(--charcoal)] transition-colors"
                        >
                            {mode === "login" ? "New here? Create an account →" : "← Have an account? Sign in"}
                        </button>
                    </motion.form>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
